import type { CalendarEvent } from './calendarEventsApi';

/**
 * TTL for the soft snapshot stashed when an interviewer is removed from
 * the roster. A re-add within the TTL repopulates events without
 * touching the network.
 *
 * Diverges from legacy `KInterviewScheduler` (1 minute) on purpose:
 * `calendar_events/trigger_fetch` hits the Google / MS Calendar APIs
 * via Sidekiq and routinely runs 5-10s end-to-end, so an extra minute
 * of grace covers the common "removed by accident, click back in"
 * pattern without forcing a fresh round-trip. Do not lower for parity.
 */
export const REMOVED_INTERVIEWER_SOFT_CACHE_TTL_MS = 2 * 60 * 1000;

type Entry = {
  events: CalendarEvent[];
  /** Folded `calendar_available` for this user/week at stash time. Kept so
   *  a re-add within the TTL restores the "calendar not connected" flag —
   *  without it the soft-cache synthesises `usersInfo` with an undefined
   *  flag, which the sticky-true fold reads as "available". */
  calendarAvailable?: boolean;
  expiresAt: number;
};

const store = new Map<string, Entry>();

/**
 * Timezone is part of the key because the BE anchors each event to the
 * timezone it was fetched with — replaying an IST snapshot under EST
 * mispositions every block and splits all-day events across two days.
 * `userId` stays first so `clearSoftInterviewerCacheForUser` can keep
 * matching on a `${userId}:` prefix.
 */
function cacheKey(
  userId: number,
  weekStartKey: string,
  timezone: string
): string {
  return `${userId}:${weekStartKey}:${timezone}`;
}

export function stashRemovedInterviewerEvents(
  userId: number,
  weekStartKey: string,
  timezone: string,
  events: CalendarEvent[],
  ttlMs: number,
  calendarAvailable?: boolean
): void {
  const k = cacheKey(userId, weekStartKey, timezone);
  store.set(k, {
    events,
    calendarAvailable,
    expiresAt: Date.now() + ttlMs,
  });
}

export function takeSoftCachedInterviewerEvents(
  userId: number,
  weekStartKey: string,
  timezone: string
): CalendarEvent[] | null {
  const k = cacheKey(userId, weekStartKey, timezone);
  const entry = store.get(k);
  if (!entry) {
    return null;
  }
  if (Date.now() > entry.expiresAt) {
    store.delete(k);
    return null;
  }
  return entry.events;
}

/** Read the stashed `calendar_available` for a re-added interviewer's week
 *  (without consuming the entry). `undefined` when no live entry exists. */
export function peekSoftCachedCalendarAvailable(
  userId: number,
  weekStartKey: string,
  timezone: string
): boolean | undefined {
  const entry = store.get(cacheKey(userId, weekStartKey, timezone));
  if (!entry || Date.now() > entry.expiresAt) {
    return undefined;
  }
  return entry.calendarAvailable;
}

export function clearSoftInterviewerCacheForUser(userId: number): void {
  const prefix = `${userId}:`;
  const keys = Array.from(store.keys());
  for (const k of keys) {
    if (k.startsWith(prefix)) {
      store.delete(k);
    }
  }
}

export function clearSoftInterviewerCacheEntry(
  userId: number,
  weekStartKey: string,
  timezone: string
): boolean {
  const k = cacheKey(userId, weekStartKey, timezone);
  if (!store.has(k)) {
    return false;
  }
  store.delete(k);
  return true;
}
